// src/pages/legal/TermsOfService.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import {
  Gavel as GavelIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  Block as BlockIcon,
  ContactMail as ContactMailIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

// Paleta de colores verde pastel
const Colors = {
  primary: '#7FB069',
  primaryLight: '#A8D5BA',
  primaryDark: '#6B8E5A',
  secondary: '#000000',
  accent: '#90C695',
  background: '#F0F8F0',
  surface: '#FFFFFF',
  text: '#000000',
  textSecondary: '#724CA7'
};

const TermsOfService = () => {
  return (
    <Box sx={{ flexGrow: 1, bgcolor: Colors.background, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `2px solid ${Colors.primaryLight}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <GavelIcon sx={{ fontSize: 40, color: Colors.primary, mr: 2 }} />
            <Typography variant="h3" sx={{ color: Colors.text, fontWeight: 'bold' }}>
              Términos y Condiciones de Uso
            </Typography>
          </Box>
          <Typography variant="h6" color={Colors.textSecondary} paragraph>
            AMPA del Bosc - Condiciones Generales de Contratación
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Fecha de última actualización:</strong> {new Date().toLocaleDateString('es-ES')}
            </Typography>
          </Alert>
        </Paper>

        {/* Información General */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 2 }} />
            1. Información General
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Los presentes Términos y Condiciones regulan el uso de los servicios ofrecidos por la Asociación de Madres y Padres de Alumnos (AMPA) del Bosc, así como el acceso y uso de su plataforma digital.
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              Datos de la Asociación
            </Typography>
            <Typography><strong>Denominación:</strong> AMPA del Bosc</Typography>
            <Typography><strong>Dirección:</strong> [Dirección de la escuela]</Typography>
            <Typography><strong>Email:</strong> ampadelbosc@gmail.com</Typography>
            <Typography><strong>Teléfono:</strong> [Teléfono de contacto]</Typography>
            <Typography><strong>CIF:</strong> [CIF de la AMPA]</Typography>
            <Typography><strong>Registro:</strong> [Número de registro de asociaciones]</Typography>
          </Box>

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Aceptación de términos:</strong> El uso de nuestros servicios implica la aceptación expresa de estos términos y condiciones. Si no está de acuerdo, le rogamos que no utilice nuestros servicios.
            </Typography>
          </Alert>
        </Paper>

        {/* Servicios Ofrecidos */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 2 }} />
            2. Servicios Ofrecidos
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            La AMPA del Bosc ofrece los siguientes servicios a través de su plataforma digital y presencialmente:
          </Typography>

          <Stack spacing={2}>
            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                💻 Plataforma Digital
              </Typography>
              <Typography variant="body2">
                • Portal web para socios y familias<br/>
                • Sistema de notificaciones y comunicaciones<br/>
                • Calendario de eventos y actividades<br/>
                • Asistente virtual para consultas (chatbot)
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🛒 Tienda Online
              </Typography>
              <Typography variant="body2">
                • Venta de uniformes escolares<br/>
                • Material escolar y libros de texto<br/>
                • Productos de la escuela<br/>
                • Pagos seguros a través de PayPal
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🎯 Eventos y Actividades
              </Typography>
              <Typography variant="body2">
                • Organización de eventos familiares<br/>
                • Actividades culturales y lúdicas<br/>
                • Talleres formativos para padres<br/>
                • Gestión de excursiones y salidas
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🛒 Tienda y Productos
              </Typography>
              <Typography variant="body2">
                • Uniformes escolares<br/>
                • Material escolar y libros<br/>
                • Productos promocionales<br/>
                • Merchandising de la AMPA
              </Typography>
            </Paper>
          </Stack>
        </Paper>

        {/* Inscripciones y Contratación */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <ContactMailIcon sx={{ mr: 2 }} />
            3. Inscripciones y Contratación
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            <strong>3.1. Proceso de Inscripción</strong>
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Typography variant="h6" color={Colors.primary}>•</Typography>
              </ListItemIcon>
              <ListItemText
                primary="Las inscripciones se realizan a través de la plataforma digital o presencialmente"
                secondary="Se requiere completar todos los campos obligatorios del formulario"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Typography variant="h6" color={Colors.primary}>•</Typography>
              </ListItemIcon>
              <ListItemText
                primary="Documentación requerida"
                secondary="DNI/NIE de los padres, certificado médico si procede, autorizaciones firmadas"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Typography variant="h6" color={Colors.primary}>•</Typography>
              </ListItemIcon>
              <ListItemText
                primary="Confirmación de plaza"
                secondary="La inscripción queda confirmada tras el pago de la reserva correspondiente"
              />
            </ListItem>
          </List>

          <Typography paragraph sx={{ mt: 3 }}>
            <strong>3.2. Prioridad en las Inscripciones</strong>
          </Typography>
          <Typography paragraph>
            En caso de plazas limitadas, se establecerá el siguiente orden de prioridad:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="1. Socios de la AMPA del curso anterior" />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. Hermanos/as de alumnos ya inscritos" />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Nuevos socios por orden de inscripción" />
            </ListItem>
            <ListItem>
              <ListItemText primary="4. No socios por orden de inscripción" />
            </ListItem>
          </List>
        </Paper>

        {/* Precios y Pagos */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <PaymentIcon sx={{ mr: 2 }} />
            4. Precios y Condiciones de Pago
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            <strong>4.1. Estructura de Precios</strong>
          </Typography>
          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              Descuentos Aplicables
            </Typography>
            <Typography>• <strong>Socios AMPA:</strong> 10% de descuento en todos los servicios</Typography>
            <Typography>• <strong>Hermanos:</strong> 5% adicional a partir del segundo hijo</Typography>
            <Typography>• <strong>Familias numerosas:</strong> Descuento adicional según legislación vigente</Typography>
            <Typography>• <strong>Situaciones especiales:</strong> Becas y ayudas previa evaluación</Typography>
          </Box>

          <Typography paragraph>
            <strong>4.2. Modalidades de Pago</strong>
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Chip label="Domiciliación" sx={{ bgcolor: Colors.primaryLight }} />
              </ListItemIcon>
              <ListItemText
                primary="Pago por domiciliación bancaria (recomendado)"
                secondary="Cargo mensual los días 5 de cada mes. Requiere mandato SEPA firmado."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="Transferencia" sx={{ bgcolor: Colors.accent }} />
              </ListItemIcon>
              <ListItemText
                primary="Transferencia bancaria"
                secondary="Pago mensual antes del día 10. Incluir nombre del alumno en concepto."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="Efectivo" sx={{ bgcolor: Colors.primaryDark, color: 'white' }} />
              </ListItemIcon>
              <ListItemText
                primary="Pago en efectivo"
                secondary="Solo para servicios puntuales. En secretaría en horario establecido."
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Importante:</strong> Los servicios se suspenderán automáticamente en caso de impago superior a 30 días. Se aplicará un recargo de 3€ por recibo devuelto.
            </Typography>
          </Alert>
        </Paper>

        {/* Cancelaciones y Bajas */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <BlockIcon sx={{ mr: 2 }} />
            5. Cancelaciones y Bajas
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            <strong>5.1. Baja Voluntaria</strong>
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="• Comunicación con 15 días de antelación por escrito"
                secondary="Enviar email a ampadelbosc@gmail.com o entregar en secretaría"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="• Finalización del servicio a final del mes en curso"
                secondary="No se admiten bajas con efectos retroactivos"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="• Reembolso proporcional si procede"
                secondary="Solo para servicios no prestados y pagos anticipados"
              />
            </ListItem>
          </List>

          <Typography paragraph sx={{ mt: 3 }}>
            <strong>5.2. Baja por Incumplimiento</strong>
          </Typography>
          <Typography paragraph>
            La AMPA se reserva el derecho de dar de baja a usuarios que:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• Incumplan reiteradamente las normas de comportamiento" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Mantengan impagos superiores a 60 días" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Proporcionen información falsa o incompleta" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Muestren conductas inadecuadas hacia el personal o otros usuarios" />
            </ListItem>
          </List>
        </Paper>

        {/* Responsabilidades */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 2 }} />
            6. Responsabilidades y Limitaciones
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            <strong>6.1. Responsabilidades de la AMPA</strong>
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• Prestar los servicios contratados con la calidad comprometida" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Mantener la confidencialidad de los datos personales" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Comunicar cualquier incidencia relevante a las familias" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Disponer de seguros de responsabilidad civil" />
            </ListItem>
          </List>

          <Typography paragraph sx={{ mt: 3 }}>
            <strong>6.2. Responsabilidades de las Familias</strong>
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• Proporcionar información veraz y actualizada" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Comunicar cambios en alergias, medicación o situación familiar" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Respetar las normas de funcionamiento de cada servicio" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Efectuar los pagos en tiempo y forma" />
            </ListItem>
          </List>

          <Typography paragraph sx={{ mt: 3 }}>
            <strong>6.3. Limitación de Responsabilidad</strong>
          </Typography>
          <Alert severity="info">
            <Typography variant="body2">
              La AMPA no se hace responsable de daños o pérdidas de objetos personales, salvo en casos de negligencia grave demostrada. Se recomienda marcar las pertenencias con el nombre del alumno.
            </Typography>
          </Alert>
        </Paper>

        {/* Fuerza Mayor */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 2 }} />
            7. Fuerza Mayor y Circunstancias Extraordinarias
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            En caso de situaciones extraordinarias (pandemia, huelgas, catástrofes naturales, etc.) que impidan la prestación normal de los servicios:
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Suspensión temporal de servicios"
                secondary="La AMPA podrá suspender servicios siguiendo las directrices de las autoridades competentes"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Adaptación de servicios"
                secondary="Se implementarán protocolos alternativos cuando sea posible (ej: servicio online, grupos reducidos)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ajuste de precios"
                secondary="Los precios se ajustarán proporcionalmente a los servicios efectivamente prestados"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Comunicación constante"
                secondary="Se mantendrá informadas a las familias de cualquier cambio a través de los canales oficiales"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Modificaciones */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 2 }} />
            8. Modificaciones de los Términos
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            La AMPA se reserva el derecho de modificar estos términos y condiciones en cualquier momento. Las modificaciones se comunicarán con al menos 30 días de antelación a través de:
          </Typography>

          <List>
            <ListItem>
              <ListItemText primary="• Publicación en la página web oficial" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Comunicación por email a todas las familias" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Circular informativa en el centro educativo" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Notificación en la aplicación móvil" />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Derecho de baja:</strong> En caso de no estar de acuerdo con las modificaciones, las familias podrán darse de baja de los servicios sin penalización alguna, comunicándolo en el plazo de 15 días desde la notificación.
            </Typography>
          </Alert>
        </Paper>

        {/* Legislación Aplicable */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <GavelIcon sx={{ mr: 2 }} />
            9. Legislación Aplicable y Jurisdicción
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Estos términos y condiciones se rigen por la legislación española vigente. En particular:
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Ley Orgánica 3/2018 de Protección de Datos Personales"
                secondary="Cumplimiento del RGPD en el tratamiento de datos"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ley 1/2007 de Asociaciones de Andalucía"
                secondary="Marco legal de funcionamiento de asociaciones"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Real Decreto Legislativo 1/2007 - Ley General de Consumidores"
                secondary="Protección de derechos de consumidores y usuarios"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ley 34/2002 de Servicios de la Sociedad de la Información"
                secondary="Comercio electrónico y servicios digitales"
              />
            </ListItem>
          </List>

          <Typography paragraph sx={{ mt: 3 }}>
            Para la resolución de cualquier controversia, las partes se someterán a la jurisdicción de los Juzgados y Tribunales del lugar donde tenga su domicilio social la AMPA.
          </Typography>
        </Paper>

        {/* Contacto */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <ContactMailIcon sx={{ mr: 2 }} />
            10. Contacto y Atención al Usuario
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Para cualquier consulta, sugerencia o reclamación relacionada con estos términos:
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              📞 Atención al Usuario
            </Typography>
            <Typography><strong>Email general:</strong> ampadelbosc@gmail.com</Typography>
            <Typography><strong>Email reclamaciones:</strong> ampadelbosc@gmail.com</Typography>
            <Typography><strong>Teléfono:</strong> [Teléfono de contacto]</Typography>
            <Typography><strong>Horario de atención:</strong> Lunes a viernes de 9:00 a 17:00</Typography>
            <Typography><strong>Atención presencial:</strong> Secretaría del centro educativo</Typography>
          </Box>
        </Paper>

        {/* Footer */}
        <Paper sx={{ p: 3, bgcolor: Colors.primaryLight, textAlign: 'center' }}>
          <Typography variant="body2" color={Colors.primaryDark}>
            Estos Términos y Condiciones constituyen el acuerdo completo entre la AMPA del Bosc y el usuario.<br/>
            Al utilizar nuestros servicios, usted declara haber leído, entendido y aceptado estos términos.<br/>
            <strong>Última actualización:</strong> {new Date().toLocaleDateString('es-ES')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfService;